import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { formatDateToDDMMYYYY } from "../pages/const";

// Register font (optional, can be customized)
Font.register({
  family: "Times New Roman",
  src: "https://db.onlinewebfonts.com/t/32441506567156636049eb850b53f02a.ttf",
});

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Times-Roman",
  },
  header: {
    marginBottom: 10,
    textAlign: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#8c1d1a",
    marginBottom: 4,
  },
  ledgerInfo: {
    fontSize: 10,
    marginBottom: 8,
    textAlign: "center",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#8c1d1a",
    marginBottom: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#8c1d1a",
    borderBottomStyle: "solid",
    width: "100%",
  },
  tableRowNoBorder: {
    flexDirection: "row",
    borderBottomWidth: 0,
    width: "100%",
  },
  tableHeaderRow: {
    backgroundColor: "#f0e6d2",
    fontWeight: "bold",
  },
  tableTotalRow: {
    backgroundColor: "#f0e6d2",
    fontWeight: "bold",
  },
  // Cell styles - 11 columns total
  cell: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
    textAlign: "center",
  },
  cellLast: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 0,
    textAlign: "center",
  },
  cellCenter: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
    textAlign: "center",
  },
  cellRight: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
    textAlign: "right",
  },
  headerCell: {
    fontSize: 10,
    fontWeight: "bold",
    padding: 4,
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
    backgroundColor: "#f0e6d2",
  },
  headerCellLast: {
    fontSize: 10,
    fontWeight: "bold",
    padding: 4,
    textAlign: "center",
    borderRightWidth: 0,
    backgroundColor: "#f0e6d2",
  },
  footer: {
    marginTop: 12,
    fontSize: 8,
    textAlign: "center",
  },
});

const SalesLedgerPDF = ({
  ledgerData,
  summaryData,
  fromDate,
  toDate,
  entireReport,
}) => {
  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="landscape">
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Sales Ledger Report</Text>
        </View>
        {/* Report Info */}
        <View style={styles.ledgerInfo}>
          {entireReport || fromDate || toDate ? (
            <Text>
              {entireReport
                ? "Entire Report"
                : `From: ${formatDateToDDMMYYYY(fromDate) || "-"} To: ${
                    formatDateToDDMMYYYY(toDate) || "-"
                  }`}
            </Text>
          ) : null}
        </View>
        {/* Ledger Table */}
        <View style={styles.table}>
          {/* Header Row 1 - Main headers: 11 columns */}
          <View
            style={[
              styles.tableRowNoBorder,
              styles.tableHeaderRow,
              { minHeight: 30 },
            ]}
          >
            {/* User Details - spans 2 columns */}
            <View
              style={{
                width: "22%",
                borderRightWidth: 1,
                borderRightColor: "#8c1d1a",
                borderBottomWidth: 1,
                borderBottomColor: "#8c1d1a",
                backgroundColor: "#f0e6d2",
                padding: 4,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Text
                style={{ fontSize: 7, fontWeight: "bold", textAlign: "center" }}
              >
                User Details
              </Text>
            </View>
            {/* INVOICE DETAILS - spans 3 columns */}
            <View
              style={{
                width: "27%",
                borderRightWidth: 1,
                borderRightColor: "#8c1d1a",
                borderBottomWidth: 1,
                borderBottomColor: "#8c1d1a",
                backgroundColor: "#f0e6d2",
                padding: 4,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Text
                style={{ fontSize: 7, fontWeight: "bold", textAlign: "center" }}
              >
                INVOICE DETAILS
              </Text>
            </View>
            {/* Empty cell for TAXABLE VALUE - merged with row 2, no bottom border */}
            <View
              style={{
                width: "11%",
                borderRightWidth: 1,
                borderRightColor: "#8c1d1a",
                borderBottomWidth: 0,
                backgroundColor: "#f0e6d2",
                padding: 4,
              }}
            ></View>
            {/* AMOUNT OF TAX - spans 3 columns */}
            <View
              style={{
                width: "27%",
                borderRightWidth: 1,
                borderRightColor: "#8c1d1a",
                borderBottomWidth: 1,
                borderBottomColor: "#8c1d1a",
                backgroundColor: "#f0e6d2",
                padding: 4,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Text
                style={{ fontSize: 7, fontWeight: "bold", textAlign: "center" }}
              >
                AMOUNT OF TAX
              </Text>
            </View>
            {/* WEIGHT - spans 2 columns */}
            <View
              style={{
                width: "13%",
                borderRightWidth: 0,
                borderBottomWidth: 1,
                borderBottomColor: "#8c1d1a",
                backgroundColor: "#f0e6d2",
                padding: 4,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Text
                style={{ fontSize: 7, fontWeight: "bold", textAlign: "center" }}
              >
                WEIGHT
              </Text>
            </View>
          </View>
          {/* Header Row 2 - Sub headers: 11 columns */}
          <View style={[styles.tableRow, styles.tableHeaderRow]}>
            <Text style={[styles.headerCell, { width: "11%" }]}>NAME</Text>
            <Text style={[styles.headerCell, { width: "11%" }]}>Phone</Text>
            <Text style={[styles.headerCell, { width: "9%" }]}>INVOICE NO</Text>
            <Text style={[styles.headerCell, { width: "9%" }]}>
              INVOICE DATE
            </Text>
            <Text style={[styles.headerCell, { width: "9%" }]}>
              INVOICE VALUE
            </Text>
            <Text style={[styles.headerCell, { width: "11%" }]}>
              TAXABLE VALUE
            </Text>
            <Text style={[styles.headerCell, { width: "9%" }]}>SGST</Text>
            <Text style={[styles.headerCell, { width: "9%" }]}>CGST</Text>
            <Text style={[styles.headerCell, { width: "9%" }]}>TOTAL TAX</Text>
            <Text style={[styles.headerCell, { width: "6.5%" }]}>
              GOLD (GM)
            </Text>
            <Text style={[styles.headerCellLast, { width: "6.5%" }]}>
              SILVER (GM)
            </Text>
          </View>
          {/* Data Rows */}
          {ledgerData.map((row, idx) => (
            <View style={styles.tableRow} key={idx}>
              <Text style={[styles.cell, { width: "11%" }]}>
                {row.name || "-"}
              </Text>
              <Text style={[styles.cell, { width: "11%" }]}>
                {row.phone || "-"}
              </Text>
              <Text style={[styles.cellCenter, { width: "9%" }]}>
                {row.invoice_no || "-"}
              </Text>
              <Text style={[styles.cellCenter, { width: "9%" }]}>
                {row.invoice_date || "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "9%" }]}>
                {row.invoice_value
                  ? Math.round(row.invoice_value).toLocaleString("en-IN")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "11%" }]}>
                {row.taxable_value
                  ? row.taxable_value
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "9%" }]}>
                {row.sgst
                  ? row.sgst.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "9%" }]}>
                {row.cgst
                  ? row.cgst.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "9%" }]}>
                {row.total_tax
                  ? row.total_tax
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "6.5%" }]}>
                {row.gold_gm > 0 ? row.gold_gm.toFixed(3) : "-"}
              </Text>
              <Text style={[styles.cellLast, { width: "6.5%" }]}>
                {row.silver_gm > 0 ? row.silver_gm.toFixed(3) : "-"}
              </Text>
            </View>
          ))}
          {/* Summary Row */}
          {summaryData && (
            <View style={[styles.tableRow, styles.tableTotalRow]}>
              <Text style={[styles.cell, { width: "11%" }]}></Text>
              <Text style={[styles.cell, { width: "11%" }]}></Text>
              <Text style={[styles.cellCenter, { width: "9%" }]}></Text>
              <Text style={[styles.cellCenter, { width: "9%" }]}></Text>
              <Text style={[styles.cellRight, { width: "9%" }]}>
                {summaryData.invoice_value
                  ? Math.round(summaryData.invoice_value).toLocaleString(
                      "en-IN"
                    )
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "11%" }]}>
                {summaryData.taxable_value
                  ? summaryData.taxable_value
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "9%" }]}>
                {summaryData.sgst
                  ? summaryData.sgst
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "9%" }]}>
                {summaryData.cgst
                  ? summaryData.cgst
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "9%" }]}>
                {summaryData.total_tax
                  ? summaryData.total_tax
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : "-"}
              </Text>
              <Text style={[styles.cellRight, { width: "6.5%" }]}>
                {summaryData.gold_gm > 0 ? summaryData.gold_gm.toFixed(3) : "-"}
              </Text>
              <Text style={[styles.cellLast, { width: "6.5%" }]}>
                {summaryData.silver_gm > 0
                  ? summaryData.silver_gm.toFixed(3)
                  : "-"}
              </Text>
            </View>
          )}
        </View>
        {/* Footer */}
        <View style={styles.footer}>
          <Text>
            Generated on:{" "}
            {formatDateToDDMMYYYY(new Date().toISOString().split("T")[0])}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default SalesLedgerPDF;
